<template>
    <div data-test-id="l-reviews-edit-participants-form">
        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.participationInclude`) }}</strong>
        </h4>

        <div class="row">
            <div class="col-md-4">
                <l-select
                    :value="participationRulesInclude"
                    :disabled="['employee', 'manager'].includes(reviewCycle.createdAsRole)"
                    :options="[
                        { value: 'all', text: $t(`reviews.admin.participationAll`) },
                        { value: 'specific', text: $t(`reviews.admin.participationSpecific`) },
                    ]"
                    @change="e => toggleParticipationRulesInclude(e.target.value)"
                />
            </div>
        </div>
        <div class="row">
            <div v-if="!reviewCycle.participationRules.include.all" class="col-md-8">
                <l-multi-filter
                    :preselected-filters="reviewCycle.participationRules.include"
                    :show-custom-attributes="true"
                    @update="updateFiltersInclude"
                />
            </div>
            <div class="col-md-1">
                <l-input-text
                    v-model="reviewCycle.participationRules.exclude.tenureInWeeksLessThan"
                    :label="``"
                    type="number"
                    :min-number="0"
                    optional
                />
            </div>
            <div
                v-b-popover.hover.top="$t(`reviews.admin.participationTenureInWeeksLessThanHelp`)"
                class="col-md-3"
            >
                <p style="margin-top: 5px">
                    <span>{{ $t(`reviews.admin.participationTenureInWeeksLessThan`) }}</span>
                    <i class="fal fa-help r-default-margin-s"></i>
                </p>
            </div>
        </div>

        <div v-show="!reviewCycle.participationRules.include.all">
            <p><l-text :text="$t(`reviews.admin.participationIncludeHelp`)" /></p>
            <hr />
        </div>

        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.participationExclude`) }}</strong>
        </h4>

        <div class="row">
            <div class="col-md-8">
                <l-multi-filter
                    :preselected-filters="reviewCycle.participationRules.exclude"
                    :show-custom-attributes="true"
                    @update="updateFiltersExclude"
                />
            </div>
        </div>

        <p><l-text :text="$t(`reviews.admin.participationExcludeHelp`)" /></p>
        <hr />

        <div v-if="error" id="error" class="alert alert-danger">
            <span>{{ error.message }}</span>
        </div>

        <div class="row">
            <div class="col-md-4 col-md-offset-8">
                <button
                    v-show="
                        reviewCycle.status.globalStatus === 'draft' &&
                            !reviewCycle.isLocked &&
                            reviewCycle.createdAsRole != 'employee'
                    "
                    class="btn btn-primary btn-block btn-raised"
                    type="button"
                    @click="next()"
                >
                    {{ $t(`reviews.admin.btnContinue`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";
import LMultiFilter from "@/components/LMultiFilter";

export default {
    components: {
        LInputText,
        LSelect,
        LText,
        LMultiFilter,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            reviewCycle: {
                participationRules: {
                    include: {
                        all: true,
                        locations: [],
                        localTeams: [],
                        users: [],
                    },
                    exclude: {
                        locations: [],
                        localTeams: [],
                        users: [],
                    },
                },
            },
            participationRulesInclude: "all",
            locationSearchList: [],
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            getCycleDetails: "reviews/cycles/details",

            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            locationList: "users/location/list",
        }),
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.reviewCycle = this.getCycleDetails(this.id);
                this.participationRulesInclude = this.reviewCycle.participationRules.include.all
                    ? "all"
                    : "specific";
            },
        },
        reviewCycle: {
            deep: true,
            handler: debounce(function handler() {
                this.update();
            }, 1000),
        },
    },
    methods: {
        updateFiltersInclude(filters) {
            this.$set(this.reviewCycle.participationRules, "include", {
                all: this.reviewCycle.participationRules.include.all,
                localTeams: filters.localTeams,
                users: filters.users,
                locations: filters.locations,
                customAttributes: filters.customAttributes,
            });
            this.update();
        },
        updateFiltersExclude(filters) {
            this.$set(this.reviewCycle.participationRules, "exclude", {
                localTeams: filters.localTeams,
                users: filters.users,
                locations: filters.locations,
                customAttributes: filters.customAttributes,
            });
            this.update();
        },

        toggleParticipationRulesInclude(value) {
            if (value === "all") {
                this.reviewCycle.participationRules.include.all = true;
            } else {
                this.reviewCycle.participationRules.include.all = false;
            }

            this.participationRulesInclude = value;
        },

        async update() {
            this.error = null;

            try {
                await this.$store.dispatch({
                    type: "reviews/cycles/update",
                    payload: {
                        reviewCycle: this.reviewCycle,
                        scope: "participants",
                    },
                });
            } catch (err) {
                if (err?.response?.data?.message) {
                    this.error = { message: err?.response?.data?.message };
                }
            }
        },

        async next() {
            await this.update();

            if (this.error === null) {
                this.$emit("next");
            }
        },
    },
};
</script>
